import cn from 'classnames';
import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import { useEffect, useRef } from 'preact/hooks';
import { type Product } from '@/domain/Product';
import useRoute from '@/widgets/hooks/useRoute';
import useMarketConfig from '@/widgets/hooks/useMarketConfig';
import styles from './ProductPurchaseSection.module.scss';
import PricingDetails from './parts/PricingDetails/PricingDetails.tsx';
import { strikethroughPriceFromProductDiscount } from '@/domain/Product.ts';
import SkeletonText from '@/widgets/components/controls/Skeleton/SkeletonText.tsx';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape.tsx';
import ViewAlternatives from './parts/ViewAlternatives/ViewAlternatives.tsx';
import { useIntersection } from '@/widgets/hooks/useIntersection.tsx';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter.tsx';
import { namespace } from '@/widgets/ProductList/Providers.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '../../../config/index.ts';
import AddToCartButtonDisplayer from '@/widgets/components/controls/AddToCartButtonDisplayer/AddToCartButtonDisplayer.tsx';

export type PurchaseSectionProps = {
  class?: string;
  product: Product;
  onImpression: () => void;
};

export default function ProductPurchaseSection({
  class: className,
  product,
  onImpression
}: PurchaseSectionProps) {
  const formatPrice = usePriceFormatter();
  const { locale } = useRoute();
  const {
    financialSettings: { defaultCurrency: currencyCode, pricingModel }
  } = useMarketConfig();
  const { displayNewAddToBagButton } = useWidgetOptions<Options>();
  const { elementRef, isVisible } = useIntersection<HTMLDivElement>();
  const impressionSent = useRef(false);
  const isCapsule = product.type === 'capsule';
  const showAlternatives = !product.inStock && isCapsule;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (isVisible && !impressionSent.current) {
      timeout = setTimeout(() => {
        onImpression();
        impressionSent.current = true;
      }, 2000); // Only send impression if visible for at least 2 seconds
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isVisible]);

  return (
    <div class={cn(styles.purchaseSection, className)} ref={elementRef}>
      <div class={styles.container}>
        {showAlternatives ? (
          <ViewAlternatives
            productId={product.id}
            alternativeProducts={product.alternativeProducts ?? []}
          />
        ) : (
          <>
            <div>
              <PriceContainer
                locale={locale}
                currencyCode={currencyCode}
                price={product.pricing.price}
                strikethroughPrice={
                  product.discount
                    ? strikethroughPriceFromProductDiscount(
                        product.pricing.price,
                        product.discount
                      )
                    : undefined
                }
                layoutMode="row"
                priceFormatter={(v) => formatPrice(v)}
              />
              {isCapsule && (
                <PricingDetails product={product} pricingModel={pricingModel} />
              )}
            </div>

            <AddToCartButtonDisplayer
              displayNewAddToBagButton={displayNewAddToBagButton}
              product={product}
              namespace={namespace}
              class={styles.button}
              size="small"
            />
          </>
        )}
      </div>
    </div>
  );
}

ProductPurchaseSection.Skeleton = function Skeleton() {
  return (
    <div class={styles.purchaseSection}>
      <div class={cn(styles.container, styles.skeletonContainer)}>
        <SkeletonText class={styles.skeletonPrice} />
        <SkeletonShape class={styles.skeletonButton} />
      </div>
    </div>
  );
};
